import { trpc } from '@/main'
import { createFileRoute } from '@tanstack/react-router'
import { reportQueryOptions, reportSearchDeps } from '@/hooks/use-report'
import { deriveProfiles } from '@/hooks/use-root-search'
import { noop } from '@tanstack/react-query'
import { teamDetail } from './-components/teams/team-detail'

/**
 * Data-owning layout for a selected team (`/teams/$teamId/*`): the teamId-keyed chrome (getTeam + members list)
 * all five tab children read, plus the REPORT query — on a leaf that key would re-key the match on every
 * ámbito/source switch and paint the leaf's skeleton over the report being read. NO component, deliberately: with
 * nothing to code-split there is no chunk for the router to await before the leaf's skeleton can paint, and a
 * re-key here paints nothing. The chrome ABOVE this match (teams.tsx's report pane, the members panel) reads it
 * with `useMatch({ from: '/_app/teams/$teamId' })`.
 */
export const Route = createFileRoute('/_app/teams/$teamId')({
  staticData: { detailPanes: teamDetail },
  // EXACTLY the search the three keys below are built from — nothing else may re-run this loader.
  loaderDeps: ({ search }) => ({ ...reportSearchDeps(search), o: search.o, od: search.od, q: search.q }),
  context: ({ deps, params }) => {
    // The team subviews are never the /report page, so isReportPage is false here; `deriveProfiles` is
    // what `useRootSearch` already applies at render, so both sides see the same `first`/`second`.
    const search = { ...deps, ...deriveProfiles(deps, params, false) }
    const teamId = Number(params.teamId)
    // The filters are whichever SELECTED profile is this team — the tab children and the chrome above
    // this match all read the field below, so this derivation exists once, here.
    const teamProfile =
      search.first?.type === 'team' && search.first.id === teamId
        ? search.first
        : search.second?.type === 'team' && search.second.id === teamId
          ? search.second
          : undefined
    return {
      teamQueryOptions: trpc.teams.getTeam.queryOptions({
        teamId,
        filters: {
          type: search.type,
          evaluation: teamProfile?.evaluation,
          members: teamProfile && 'members' in teamProfile ? teamProfile.members : undefined,
          sort: search.o,
          sortDirection: search.od,
        },
      }),
      // The members list as a visit lands on it. The panel that renders it sits in the parent
      // `teams.tsx`, above this match, so it calls the same factory with its live `q`.
      teamMembersInfiniteQueryOptions: trpc.teams.searchMembersInfinite.infiniteQueryOptions(
        { teamId: Number(params.teamId), q: deps.q, o: deps.o, od: deps.od, type: deps.type },
        { getNextPageParam: (lastPage) => lastPage.nextCursor },
      ),
      reportQueryOptions: reportQueryOptions(deps, params),
    }
  },
  loader: ({ context }) => {
    // The panes teams.tsx renders for a selected team: a row's hover fetches their chunk with the data.
    teamDetail.preload().catch(noop)
    context.queryClient.query(context.teamQueryOptions).catch(noop)
    context.queryClient.infiniteQuery(context.teamMembersInfiniteQueryOptions).catch(noop)
    if (context.reportQueryOptions) context.queryClient.query(context.reportQueryOptions).catch(noop)
  },
})
